'use client';

import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { TermAccordion } from '@/app/components/sections/service/service-center/terms/accordion';
import { ScriptFaqJsonLd } from '@/app/components/sections/service/service-center/terms/script-faq-json-ld';
import type { ServiceCenterTermsWithCanonicalProps } from '@/app/components/sections/service/service-center/terms/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export const ServiceCenterTermsSection = ({
  id = 'terms-and-conditions',
  jsonLdIdSuffix = 'terms',
  testId = 'terms',
  className,
  heading,
  items = [],
  canonicalUrl,
  enableJsonLd = true,
  accordionMode = 'individual',
  defaultExpandedIds,
  enableScrollIntoView = false,
}: ServiceCenterTermsWithCanonicalProps) => {
  useRevealAnim({ defaultBaseDelay: 0.04 });

  if (items.length === 0) return null;

  return (
    <Section
      id={id}
      aria-labelledby={`${id}-heading`}
      size="none"
      className={cn(
        'bg-silver-100 relative w-full scroll-mt-14 overflow-hidden py-7 sm:py-10 xl:py-16',
        className
      )}
      {...createDataAttributes({ test: `${testId}-section` })}
      data-revealanim-group
    >
      {!!enableJsonLd && !!canonicalUrl && (
        <ScriptFaqJsonLd
          idSuffix={jsonLdIdSuffix}
          items={items}
          canonicalUrl={canonicalUrl}
          heading={heading}
        />
      )}
      <Container>
        <h2
          id={`${id}-heading`}
          data-revealanim
          className="text-sub-h1 font-noto-bold mb-6 text-gray-900 lg:mb-8"
          dangerouslySetInnerHTML={{ __html: heading }}
          {...createDataAttributes({ test: `${testId}-heading` })}
        />
        <TermAccordion
          defaultExpandedIds={defaultExpandedIds}
          items={items}
          accordionMode={accordionMode}
          enableScrollIntoView={enableScrollIntoView}
          testId={testId}
          data-revealanim
        />
      </Container>
    </Section>
  );
};

ServiceCenterTermsSection.displayName = 'ServiceCenterTermsSection';
